import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, Check } from "lucide-react";

const STAKEHOLDERS = [
  { id: "carrier", role: "Carrier", concern: "Ready to depart — but who may authorise release?", pos: { left: "50%", top: "7%" } },
  { id: "inspector", role: "Inspector", concern: "Holds the inspection evidence others need.", pos: { left: "11%", top: "42%" } },
  { id: "insurer", role: "Insurer", concern: "One policy condition remains unclear.", pos: { left: "89%", top: "42%" } },
  { id: "customer", role: "Customer", concern: "Latest release status is disputed.", pos: { left: "23%", top: "90%" } },
  { id: "operator", role: "Operator", concern: "Shows the hold; cannot clear it alone.", pos: { left: "77%", top: "90%" } },
];
const MARKERS = [
  "Status disputed",
  "Policy condition unclear",
  "Inspection evidence held elsewhere",
];
const PROOF = ["Seal status", "Inspection validity", "Policy condition"];
const ROUTE = ["Evidence stays with owner", "Authorised role reviews", "Approve / Refuse / Escalate", "Receipt"];
const EASE = [0.22, 0.61, 0.36, 1];

const FRAGMENTS = [
  [11, 42, 89, 42], [50, 7, 23, 90], [50, 7, 77, 90],
  [23, 90, 89, 42], [77, 90, 11, 42], [11, 42, 77, 90],
];

export default function HeroFrame() {
  const [active, setActive] = useState(null);
  const node = STAKEHOLDERS.find((s) => s.id === active);

  return (
    <div className="frame-wrap" data-testid="hero-frame">
      <div className="frame">
        {/* ZONE 1 — stalled world */}
        <motion.div className="zone zone--stalled"
          initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: EASE }}>
          <span className="zone__label zone__label--stall">The decision nobody can move</span>
          <span className="zone__sub">Stalled world</span>
          <div className="z1-stage">
            <svg className="z1-frag" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
              {FRAGMENTS.map((f, i) => (
                <line key={i} x1={f[0]} y1={f[1]} x2={f[2]} y2={f[3]}
                      style={{ animationDelay: (i * 0.4) + "s" }} />
              ))}
            </svg>
            <div className="z1-event">
              <div className="z1-event__t">09:12 — Seal anomaly flagged</div>
              <div className="z1-event__q">Can the consignment move?</div>
              <span className="z1-event__s">UNRESOLVED</span>
            </div>
            {STAKEHOLDERS.map((s) => (
              <button key={s.id} type="button" style={s.pos}
                className={"z1-node" + (active === s.id ? " is-active" : "")}
                data-testid={"hero-node-" + s.id}
                onMouseEnter={() => setActive(s.id)}
                onMouseLeave={() => setActive(null)}
                onClick={() => setActive((a) => (a === s.id ? null : s.id))}>
                {s.role}
              </button>
            ))}
          </div>
          <div className="z1-concern" aria-live="polite">
            {node ? (
              <div className="z1-active-concern" data-testid="hero-concern">
                <b>{node.role}</b><span>{node.concern}</span>
              </div>
            ) : (
              <div className="z1-amb">
                {MARKERS.map((a) => <span key={a}>{a}</span>)}
              </div>
            )}
          </div>
        </motion.div>

        <motion.div className="frame__conn" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 0.55, duration: 0.4 }}>
          <ArrowRight size={22} />
        </motion.div>

        {/* ZONE 2 — governed request (turning point) */}
        <motion.div className="zone zone--request"
          initial={{ opacity: 0, y: 16, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.7, ease: EASE }}>
          <div className="req-card" data-testid="hero-request">
            <span className="req-card__tag">Governed request</span>
            <div className="req-card__t">Conditional release decision</div>
            <p className="req-card__lead">Request a decision, not blanket access.</p>
            <div className="req-card__k">Minimum proof</div>
            <div className="req-card__proof">
              {PROOF.map((p) => (
                <span key={p}><i><Check size={11} /></i>{p}</span>
              ))}
            </div>
          </div>
        </motion.div>

        <motion.div className="frame__conn" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          transition={{ delay: 1.0, duration: 0.4 }}>
          <ArrowRight size={22} />
        </motion.div>

        {/* ZONE 3 — governed route */}
        <motion.div className="zone zone--governed"
          initial="hidden" animate="show"
          variants={{ show: { transition: { staggerChildren: 0.13, delayChildren: 1.05 } } }}>
          <motion.span className="zone__label zone__label--gov"
            variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }}>
            The governed route
          </motion.span>
          <motion.span className="zone__sub"
            variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }}>
            Evidence stays with owners
          </motion.span>
          <div className="z3-route">
            {ROUTE.map((step, i) => (
              <React.Fragment key={step}>
                <motion.div className="z3-step"
                  variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}>
                  <b>{i + 1}</b><span>{step}</span>
                </motion.div>
                {i < ROUTE.length - 1 && (
                  <motion.div className="z3-arrow"
                    variants={{ hidden: { opacity: 0 }, show: { opacity: 0.7 } }}>
                    <ArrowDown size={15} />
                  </motion.div>
                )}
              </React.Fragment>
            ))}
            <motion.div className="z3-outcome" data-testid="hero-outcome"
              variants={{ hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } }}>
              <p className="z3-outcome__t">Conditional release authorised</p>
              <p className="z3-outcome__s">Decision, authority and basis recorded.</p>
              <p className="z3-illus">Illustrative governed outcome — not a live KATLAS-signed receipt.</p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
